import { useSelector } from 'react-redux';

import { hasFix } from '../../../../../shared/vulns/remediation';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import type { UpgradeDistance } from '../../../../../shared/vulns/types';

export type FossabotFixOption = {
  /** Fix version string. */
  version: string;
  /** Upgrade distance of this fix. Null when it could not be calculated. */
  distance: UpgradeDistance | null;
  /** True when this option is the complete fix. */
  isComplete: boolean;
};

export type UseFossabotFixOptionsReturn = {
  /** Available fix options, ordered partial before complete. */
  options: FossabotFixOption[];
  /** CVE identifier of the active vulnerability. */
  cve: string;
};

/**
 * Reads the active vulnerability issue from Redux and derives the list of fix
 * options fossabot can offer. Includes the partial fix and/or the complete
 * fix whenever each exists (via hasFix); when both exist and share the same
 * version, only the complete fix is kept.
 */
export const useFossabotFixOptions = (): UseFossabotFixOptionsReturn => {
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails);

  if (issue?.type !== 'vulnerability') {
    return { options: [], cve: '' };
  }

  const { completeFix, completeFixDistance, partialFix, partialFixDistance } = issue.remediation;

  const hasPartialFix = hasFix(partialFix);
  const hasCompleteFix = hasFix(completeFix);
  const isSameVersion = hasPartialFix && hasCompleteFix && partialFix === completeFix;

  const options: FossabotFixOption[] = [];

  if (hasPartialFix && !isSameVersion) {
    options.push({
      version: partialFix,
      distance: partialFixDistance ?? null,
      isComplete: false,
    });
  }

  if (hasCompleteFix) {
    options.push({
      version: completeFix,
      distance: completeFixDistance ?? null,
      isComplete: true,
    });
  }

  return {
    options,
    cve: issue.cve,
  };
};
